import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Database, Target, Mail, Share2, Clock, UserCheck, Cookie, MessageSquare } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#1a1f2e]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-[#1a1f2e]/95 backdrop-blur-lg border-b border-white/5 z-50">
        <div className="max-w-[1280px] mx-auto h-full flex items-center justify-between px-4 lg:px-8">
          <Link to="/" className="text-xl font-bold text-white tracking-tight">Casino<span className="text-[#4d7cfe]">IQ</span></Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="px-4 py-2 text-sm font-medium text-[#a0a8b8] hover:text-white transition-colors">Log In</Link>
            <Link to="/signup" className="px-5 py-2 rounded-lg bg-[#4d7cfe] text-white text-sm font-semibold hover:brightness-110 transition-all">Get Started</Link>
          </div>
        </div>
      </header>

      <div className="pt-28 pb-20 px-4" data-testid="privacy-page">
        <div className="max-w-3xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#a0a8b8] hover:text-[#4d7cfe] transition-colors mb-8" data-testid="privacy-back-link">
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>

          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-2" style={{ letterSpacing: '-0.02em' }} data-testid="privacy-heading">Privacy Policy</h1>
          <p className="text-[#a0a8b8] text-sm mb-12">Last updated: February 2026</p>

          <div className="space-y-10">
            {/* Section 1 */}
            <section className="bg-[#20263a] rounded-xl p-8 border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#4d7cfe]/10 flex items-center justify-center"><Database className="h-5 w-5 text-[#4d7cfe]" /></div>
                <h2 className="text-xl font-semibold text-white">1. Data We Collect</h2>
              </div>
              <ul className="text-[#a0a8b8] text-[15px] leading-relaxed space-y-2 list-disc list-inside">
                <li>Email address (required for account)</li>
                <li>Tool usage data (calculations, quiz results)</li>
                <li>Session analytics (pages visited, time on site)</li>
                <li>IP address and browser data (security purposes)</li>
              </ul>
            </section>

            {/* Section 2 */}
            <section className="bg-[#20263a] rounded-xl p-8 border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#8b5cf6]/10 flex items-center justify-center"><Target className="h-5 w-5 text-[#8b5cf6]" /></div>
                <h2 className="text-xl font-semibold text-white">2. How We Use Your Data</h2>
              </div>
              <ul className="text-[#a0a8b8] text-[15px] leading-relaxed space-y-2 list-disc list-inside">
                <li>Deliver platform tools and features</li>
                <li>Send promotional emails you consented to (casino partner offers, tool updates, tips)</li>
                <li>Improve platform features via usage analytics</li>
                <li>Prevent fraud and abuse</li>
              </ul>
            </section>

            {/* Section 3 */}
            <section className="bg-[#20263a] rounded-xl p-8 border border-white/10 border-l-4 border-l-[#f59e0b]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#f59e0b]/10 flex items-center justify-center"><Mail className="h-5 w-5 text-[#f59e0b]" /></div>
                <h2 className="text-xl font-semibold text-white">3. Email Marketing</h2>
              </div>
              <div className="text-[#a0a8b8] text-[15px] leading-relaxed space-y-3">
                <p>By creating an account, you consent to receive:</p>
                <ul className="space-y-2 list-disc list-inside">
                  <li>Welcome emails from CasinoIQ</li>
                  <li>Promotional offers from casino partners</li>
                  <li>Weekly tool tips and gambling insights</li>
                </ul>
                <p className="text-white/70 font-medium mt-4">Unsubscribe anytime via the link in any email or in Account Settings.</p>
              </div>
            </section>

            {/* Section 4 */}
            <section className="bg-[#20263a] rounded-xl p-8 border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#10b981]/10 flex items-center justify-center"><Share2 className="h-5 w-5 text-[#10b981]" /></div>
                <h2 className="text-xl font-semibold text-white">4. Data Sharing</h2>
              </div>
              <div className="text-[#a0a8b8] text-[15px] leading-relaxed space-y-3">
                <p>We share data with:</p>
                <ul className="space-y-2 list-disc list-inside">
                  <li>Casino affiliate partners (email, signup date only when you click affiliate links)</li>
                  <li>Analytics providers (anonymized)</li>
                </ul>
                <p className="text-[#10b981] font-medium mt-4">We NEVER sell your personal data.</p>
              </div>
            </section>

            {/* Section 5 */}
            <section className="bg-[#20263a] rounded-xl p-8 border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#4d7cfe]/10 flex items-center justify-center"><Clock className="h-5 w-5 text-[#4d7cfe]" /></div>
                <h2 className="text-xl font-semibold text-white">5. Data Retention</h2>
              </div>
              <p className="text-[#a0a8b8] text-[15px] leading-relaxed">
                Account data is retained while your account is active. Delete your account and all data will be permanently deleted within 30 days.
              </p>
            </section>

            {/* Section 6 */}
            <section className="bg-[#20263a] rounded-xl p-8 border border-white/10 border-l-4 border-l-[#4d7cfe]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#4d7cfe]/10 flex items-center justify-center"><UserCheck className="h-5 w-5 text-[#4d7cfe]" /></div>
                <h2 className="text-xl font-semibold text-white">6. Your Rights (GDPR)</h2>
              </div>
              <ul className="text-[#a0a8b8] text-[15px] leading-relaxed space-y-2 list-disc list-inside">
                <li><strong className="text-white">Access your data:</strong> Account Settings</li>
                <li><strong className="text-white">Delete your data:</strong> Account Settings &rarr; Delete Account</li>
                <li><strong className="text-white">Unsubscribe:</strong> Account Settings &rarr; Email Preferences</li>
                <li><strong className="text-white">Contact us:</strong> <a href="mailto:support@casinoiq.com" className="text-[#4d7cfe] hover:underline">support@casinoiq.com</a></li>
              </ul>
            </section>

            {/* Section 7 */}
            <section className="bg-[#20263a] rounded-xl p-8 border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#8b5cf6]/10 flex items-center justify-center"><Cookie className="h-5 w-5 text-[#8b5cf6]" /></div>
                <h2 className="text-xl font-semibold text-white">7. Cookies</h2>
              </div>
              <p className="text-[#a0a8b8] text-[15px] leading-relaxed">
                We use essential cookies for authentication and analytics cookies to improve the platform.
              </p>
            </section>

            {/* Contact */}
            <section className="bg-[#20263a] rounded-xl p-8 border border-[#4d7cfe]/30">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#4d7cfe]/10 flex items-center justify-center"><MessageSquare className="h-5 w-5 text-[#4d7cfe]" /></div>
                <h2 className="text-xl font-semibold text-white">Contact</h2>
              </div>
              <p className="text-[#a0a8b8] text-[15px]">
                For privacy questions, reach us at <a href="mailto:support@casinoiq.com" className="text-[#4d7cfe] hover:underline">support@casinoiq.com</a>
              </p>
            </section>
          </div>

          <div className="mt-12 text-center">
            <Link to="/terms" className="text-sm text-[#4d7cfe] hover:underline" data-testid="privacy-to-terms">View our Terms of Service</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
